import React, { useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import TradeJournalLockoutActions, { useNarrowTradeHeader } from '../TradeJournalLockoutActions';
import { hasTradePlatformAccess } from '../../utils/authAccess';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { BoxArrowRight, Bug, MoonStars, Sun } from 'react-bootstrap-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { AuthContext } from '../../contexts/AuthContext';
import { TradingContext } from '../../contexts/TradingContext';
import { MenuCollapsibleSection } from '../shared/MenuCollapsibleSection';
import OutlinePrimaryButton from '../shared/OutlinePrimaryButton';
import analyticsService from '../../services/analyticsService';
import { isTrackTier } from '../../utils/authAccess';
import { getSidebarPanelColors } from '../../utils/sidebarPanelColors';
import {
  PRO_GOLD,
  NAV_ACCENT_RED,
  getNavBadgeStyle,
  buildDashboardAccountSection,
  buildDashboardPrimarySections,
  DASHBOARD_LEARNING_SECTION,
  getTradeRoute,
} from './dashboardNavConfig';
import { DashboardNavIcon } from './dashboardNavIcons';
import { canAccessTradeWorkspace, canAccessMarketDataTools, isBrokerLinked } from '../../utils/brokerConnection';
import BrokerConnectionAction from '../broker/BrokerConnectionAction';

function SidebarNavBadge({ children, variant = 'rose' }) {
  return <span style={getNavBadgeStyle(variant)}>{children}</span>;
}

export const SIDEBAR_WIDTH = 280;

const navLinkStyle = (panel, active) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 8,
  width: '100%',
  boxSizing: 'border-box',
  padding: '8px 12px',
  fontSize: 14,
  fontWeight: active ? 600 : 500,
  color: active ? panel.primary : panel.text,
  background: active ? `${panel.primary}18` : 'transparent',
  border: 'none',
  borderLeft: active ? `3px solid ${panel.primary}` : '3px solid transparent',
  textAlign: 'left',
  cursor: 'pointer',
  textDecoration: 'none',
  transition: 'background 0.15s, color 0.15s, border-color 0.15s',
  borderRadius: 0,
  lineHeight: 1.4,
  outline: 'none',
  flexShrink: 0,
});

function SidebarLink({
  to,
  label,
  location,
  navigate,
  onNavigate,
  exact = true,
  badge,
  badgeVariant,
  accent,
  icon,
  panel,
  addTo,
  addLabel,
}) {
  const active = exact
    ? location.pathname === to
    : location.pathname === to || location.pathname.startsWith(to + '/');
  const accentColor = accent === 'red' ? NAV_ACCENT_RED : accent === 'gold' ? PRO_GOLD : null;
  const labelColor = accentColor && !active ? accentColor : active ? panel.primary : panel.text;
  const labelContent = (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flexWrap: 'wrap' }}>
      {icon ? <DashboardNavIcon name={icon} size={16} style={{ color: labelColor }} /> : null}
      <span style={{ minWidth: 0 }}>{label}</span>
      {badge ? <SidebarNavBadge variant={badgeVariant || 'rose'}>{badge}</SidebarNavBadge> : null}
    </span>
  );

  if (!addTo) {
    return (
      <button
        type="button"
        onClick={() => {
          onNavigate?.(to);
          navigate(to);
        }}
        style={{
          ...navLinkStyle(panel, active),
          ...(accentColor && !active ? { color: accentColor } : {}),
        }}
      >
        {labelContent}
      </button>
    );
  }

  return (
    <div
      style={{
        ...navLinkStyle(panel, active),
        padding: 0,
        cursor: 'default',
      }}
    >
      <button
        type="button"
        onClick={() => {
          onNavigate?.(to);
          navigate(to);
        }}
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 4px 8px 9px',
          background: 'transparent',
          border: 'none',
          color: labelColor,
          fontSize: 14,
          fontWeight: active ? 600 : 500,
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        {labelContent}
      </button>
      <button
        type="button"
        data-testid="new-ai-alert"
        aria-label={addLabel || 'Add'}
        title={addLabel || 'Add'}
        onClick={() => navigate(addTo)}
        style={{
          marginRight: 10,
          width: 22,
          height: 22,
          flexShrink: 0,
          border: 'none',
          borderRadius: 6,
          background: PRO_GOLD,
          color: '#111111',
          fontWeight: 700,
          fontSize: 16,
          lineHeight: 1,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        +
      </button>
    </div>
  );
}

function ConfigurationHeaderAction({ action, panel, navigate, onNavigate }) {
  if (!action || action.type !== 'link') return null;
  return (
    <OutlinePrimaryButton
      size="sm"
      onClick={() => {
        onNavigate?.(action.to);
        navigate(action.to);
      }}
      style={{ fontSize: 11, padding: '4px 10px', minHeight: 26, borderRadius: 6 }}
      aria-label={action.label}
    >
      {action.label}
    </OutlinePrimaryButton>
  );
}

function SidebarNavSection({ section, location, navigate, onNavigate, panel }) {
  const sectionColors = {
    borderLight: panel.border,
    textLight: panel.textLight,
    textMuted: panel.textMuted,
  };

  const headerTrailing = section.headerAction ? (
    <ConfigurationHeaderAction
      action={section.headerAction}
      panel={panel}
      navigate={navigate}
      onNavigate={onNavigate}
    />
  ) : null;

  return (
    <MenuCollapsibleSection
      title={section.title}
      defaultOpen={section.defaultOpen}
      isFirst={section.isFirst}
      colors={sectionColors}
      headerTrailing={headerTrailing}
      titleStyle={{ padding: '10px 12px 8px', color: panel.textLight, fontSize: 12 }}
    >
      {section.items.map((item) => {
        if (item.type === 'upgrade') {
          return (
            <button
              key="upgrade"
              type="button"
              onClick={() => {
                onNavigate?.('/pricing');
                navigate('/pricing');
              }}
              style={{
                ...navLinkStyle(panel, false),
                color: PRO_GOLD,
                fontWeight: 700,
              }}
            >
              Upgrade to Trade
            </button>
          );
        }
        if (item.type === 'link') {
          return (
            <SidebarLink
              key={item.to + item.label}
              to={item.to}
              label={item.label}
              location={location}
              navigate={navigate}
              onNavigate={onNavigate}
              exact={item.exact !== false}
              badge={item.badge}
              badgeVariant={item.badgeVariant}
              accent={item.accent}
              icon={item.icon}
              panel={panel}
              addTo={item.addTo}
              addLabel={item.addLabel}
            />
          );
        }
        return null;
      })}
    </MenuCollapsibleSection>
  );
}

/**
 * Persistent right-side navigation for DashboardShell and desktop Trade.
 */
export default function DashboardSidebar({ collapsed, onToggleCollapse }) {
  const { colors, themeName, toggleTheme } = useTheme();
  const panel = getSidebarPanelColors(themeName, colors);
  const { user, logout } = useContext(AuthContext);
  const { selectedAccount } = useContext(TradingContext);
  const navigate = useNavigate();
  const location = useLocation();

  const isSuperuser = user?.superuser === true;
  const essentials = isTrackTier(user);
  const showUpgradeToPro = essentials;
  const tradeRoute = getTradeRoute(user);
  const showTrade = canAccessTradeWorkspace(user, selectedAccount) !== false;

  const isTradePage = location.pathname === '/trade' || location.pathname.startsWith('/trade/');
  const isNarrowTradeHeader = useNarrowTradeHeader();
  const hasTradeAccess = hasTradePlatformAccess(user);
  const showTradeQuickActions = showTrade && isTradePage && isNarrowTradeHeader && hasTradeAccess;

  const primarySections = buildDashboardPrimarySections(tradeRoute, {
    showTrade,
    showProBadges: essentials,
    showMarketDataPages: canAccessMarketDataTools(user, selectedAccount),
    showPositionsOverview: isBrokerLinked(user),
    showBacktest: isSuperuser,
    showRsStudy: isSuperuser,
  });
  const accountSection = buildDashboardAccountSection({ showUpgradeToPro, isSuperuser });

  const handleNavigate = (to) => {
    const isNavigatingToTrade = typeof to === 'string' && (to === '/trade' || to.startsWith('/trade/'));
    if (!isNavigatingToTrade) return;
    if (!collapsed && onToggleCollapse) onToggleCollapse();
  };

  const sidebarStyle = {
    width: collapsed ? 0 : SIDEBAR_WIDTH,
    minWidth: collapsed ? 0 : SIDEBAR_WIDTH,
    maxWidth: collapsed ? 0 : SIDEBAR_WIDTH,
    overflow: 'hidden',
    height: '100%',
    background: panel.bg,
    borderLeft: `1px solid ${panel.border}`,
    display: 'flex',
    flexDirection: 'column',
    transition: 'width 0.2s ease, min-width 0.2s ease',
    flexShrink: 0,
    color: panel.text,
  };

  return (
    <aside style={sidebarStyle} aria-label="Main navigation">
      <div
        className="dashboard-sidebar-nav-scroll"
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '8px 0 12px',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        {showTradeQuickActions && <TradeJournalLockoutActions variant="sidebar" panelColors={panel} />}
        {primarySections.map((section) => (
          <SidebarNavSection
            key={section.title + (section.isFirst ? '-first' : '')}
            section={section}
            location={location}
            navigate={navigate}
            onNavigate={handleNavigate}
            panel={panel}
          />
        ))}

        <SidebarNavSection
          section={DASHBOARD_LEARNING_SECTION}
          location={location}
          navigate={navigate}
          onNavigate={handleNavigate}
          panel={panel}
        />

        <SidebarNavSection
          section={accountSection}
          location={location}
          navigate={navigate}
          onNavigate={handleNavigate}
          panel={panel}
        />
      </div>

      {/* Pinned above footer: connected brokers + add/connect CTA */}
      {!collapsed && (
        <div
          style={{
            flexShrink: 0,
            padding: '10px 14px 12px',
            borderTop: `1px solid ${panel.border}`,
            background: panel.bg,
          }}
        >
          <BrokerConnectionAction variant="sidebar" />
        </div>
      )}

      {/* Pinned footer: account email + Report Bug + logout */}
      <div
        style={{
          flexShrink: 0,
          borderTop: `1px solid ${panel.border}`,
          padding: '12px 14px 14px',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          minWidth: 0,
          background: panel.bgFooter,
        }}
      >
        {!collapsed && user?.email && (
          <div
            title={user.email}
            style={{
              fontSize: 12,
              fontWeight: 500,
              color: colors.textMuted,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: 1.3,
              padding: '0 2px',
            }}
          >
            {user.email}
          </div>
        )}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
            minWidth: 0,
          }}
        >
        {!collapsed && (
          <button
            type="button"
            onClick={() => toggleTheme?.()}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: '0 0 auto',
              width: 36,
              height: 36,
              padding: 0,
              background: 'transparent',
              border: `1px solid ${panel.border}`,
              borderRadius: 8,
              color: panel.text,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
              transition: 'box-shadow 0.15s, filter 0.15s',
              outline: 'none',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.filter = 'brightness(1.06)';
              e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.16)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.filter = 'none';
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.12)';
            }}
            aria-label={themeName === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            title={themeName === 'dark' ? 'Theme: Dark' : 'Theme: Light'}
          >
            {themeName === 'dark' ? (
              <MoonStars size={16} style={{ color: panel.text }} />
            ) : (
              <Sun size={16} style={{ color: panel.text }} />
            )}
          </button>
        )}
        {!collapsed && (
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event('stateInspector:toggle'))}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              flex: 1,
              minWidth: 0,
              padding: '8px 12px',
              background: `linear-gradient(135deg, rgba(99, 102, 241, 0.08), rgba(99, 102, 241, 0.03))`,
              border: `1px solid ${panel.border}`,
              borderRadius: 8,
              color: panel.text,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
              transition: 'box-shadow 0.15s, filter 0.15s',
              outline: 'none',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.filter = 'brightness(1.06)';
              e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.16)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.filter = 'none';
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.12)';
            }}
            aria-label="Report Bug"
          >
            <Bug size={16} style={{ color: panel.text }} />
            Bug
          </button>
        )}
        {!collapsed && (
          <button
            type="button"
            onClick={() => {
              analyticsService.trackTradingEvent('sidebar_logout_clicked', { user_id: user?.id });
              logout?.();
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              flex: 1,
              minWidth: 0,
              padding: '8px 12px',
              background: 'transparent',
              border: `1px solid ${panel.border}`,
              borderRadius: 8,
              color: panel.text,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
              transition: 'box-shadow 0.15s, filter 0.15s',
              outline: 'none',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.filter = 'brightness(1.06)';
              e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.16)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.filter = 'none';
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.12)';
            }}
            aria-label="Logout"
          >
            <BoxArrowRight size={16} style={{ color: panel.text }} />
            Logout
          </button>
        )}
        </div>
      </div>
    </aside>
  );
}
